import React from "react";
import Swal from "sweetalert2";

import {
    pdf,
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet
} from "@react-pdf/renderer";

import SchoolLogo from "./savationChristLogo.png";

/* ============================================================
   PREMIUM COLOR SYSTEM
============================================================ */

const COLORS = {
    primary: "#111827",
    secondary: "#374151",

    orange: "#f97316",
    orangeDark: "#ea580c",
    orangeLight: "#fff7ed",

    blue: "#2563eb",
    blueLight: "#eff6ff",

    green: "#16a34a",
    greenLight: "#ecfdf5",

    red: "#dc2626",
    redLight: "#fef2f2",

    gray: "#6b7280",
    grayLight: "#f8fafc",

    border: "#e5e7eb",

    white: "#ffffff",

    text: "#1f2937",
    textLight: "#6b7280"
};


/* ============================================================
   PDF STYLES
============================================================ */

const styles = StyleSheet.create({

    /* ================= PAGE ================= */

    page: {
        paddingTop: 28,
        paddingBottom: 65,
        paddingHorizontal: 28,

        backgroundColor: COLORS.white,

        fontFamily: "Helvetica",

        fontSize: 8,

        color: COLORS.text
    },


    /* ========================================================
       HEADER
    ======================================================== */

    headerContainer: {
        borderWidth: 1,
        borderColor: COLORS.border,

        borderRadius: 10,

        padding: 16,

        backgroundColor: COLORS.grayLight,

        marginBottom: 14
    },

    headerTop: {
        flexDirection: "row",

        alignItems: "center"
    },

    logo: {
        width: 62,
        height: 62,

        marginRight: 14,

        objectFit: "contain"
    },

    schoolDetails: {
        flex: 1
    },

    schoolName: {
        fontSize: 20,

        fontWeight: "bold",

        color: COLORS.primary
    },

    schoolAddress: {
        marginTop: 3,

        fontSize: 8,

        color: COLORS.textLight
    },

    schoolPhone: {
        marginTop: 2,

        fontSize: 8,

        color: COLORS.textLight
    },

    reportTitle: {
        marginTop: 10,

        fontSize: 15,

        fontWeight: "bold",

        color: COLORS.primary
    },

    divider: {
        marginTop: 10,

        marginBottom: 10,

        borderBottomWidth: 1,

        borderBottomColor: COLORS.border
    },

    infoRow: {
        flexDirection: "row",

        justifyContent: "space-between"
    },

    infoBox: {
        width: "48%"
    },

    infoLabel: {
        fontSize: 7,

        color: COLORS.textLight
    },

    infoValue: {
        marginTop: 2,

        fontSize: 9,

        fontWeight: "bold",

        color: COLORS.primary
    },


    /* ========================================================
       SUMMARY CARDS
    ======================================================== */

    summaryContainer: {
        flexDirection: "row",

        justifyContent: "space-between",

        marginBottom: 14
    },

    summaryCard: {
        width: "24%",

        borderWidth: 1,

        borderColor: COLORS.border,

        borderRadius: 8,

        padding: 8
    },

    summaryTitle: {
        fontSize: 7,

        color: COLORS.textLight,

        marginBottom: 5
    },

    summaryValue: {
        fontSize: 11,

        fontWeight: "bold"
    },

    summaryBlue: {
        backgroundColor: COLORS.blueLight
    },

    summaryOrange: {
        backgroundColor: COLORS.orangeLight
    },

    summaryGreen: {
        backgroundColor: COLORS.greenLight
    },

    summaryRed: {
        backgroundColor: COLORS.redLight
    },


    /* ========================================================
       TABLE
    ======================================================== */

    tableSection: {
        width: "100%"
    },

    tableHeader: {
        flexDirection: "row",

        width: "100%",

        minHeight: 28,

        alignItems: "center",

        backgroundColor: COLORS.primary,

        color: COLORS.white,

        borderWidth: 1,

        borderColor: COLORS.primary
    },

    tableHeaderCell: {
        fontSize: 6.5,

        fontWeight: "bold",

        textAlign: "center",

        paddingVertical: 5,

        paddingHorizontal: 2
    },

    row: {
        flexDirection: "row",

        width: "100%",

        minHeight: 30,

        alignItems: "center",

        borderLeftWidth: 1,

        borderRightWidth: 1,

        borderBottomWidth: 1,

        borderColor: COLORS.border,

        paddingVertical: 3
    },

    alternateRow: {
        backgroundColor: "#fcfcfd"
    },

    cell: {
        fontSize: 6.5,

        textAlign: "center",

        paddingHorizontal: 2,

        flexShrink: 1
    },

    studentName: {
        fontWeight: "bold",

        color: COLORS.primary
    },

    amountPaid: {
        color: COLORS.green,

        fontWeight: "bold",

        fontSize: 6.5
    },

    amountPaidLrd: {
        marginTop: 1,

        fontSize: 5.5,

        color: COLORS.gray
    },

    currencyLabel: {
        marginTop: 1,

        fontSize: 5,

        fontWeight: "bold",

        color: COLORS.blue
    },


    /* ========================================================
       COLUMN WIDTHS
       
       TOTAL = 100%
       
       4 + 19 + 10 + 11 + 13 + 11 + 11 + 11 + 10 = 100
    ======================================================== */

    c1: {
        width: "4%"
    },

    c2: {
        width: "19%"
    },

    c3: {
        width: "10%"
    },

    c4: {
        width: "11%"
    },

    c5: {
        width: "13%"
    },

    c6: {
        width: "11%"
    },

    c7: {
        width: "11%"
    },

    c8: {
        width: "11%"
    },

    c9: {
        width: "10%"
    },


    /* ========================================================
       TOTAL ROW
    ======================================================== */

    totalRow: {
        flexDirection: "row",

        width: "100%",

        minHeight: 32,

        alignItems: "center",

        backgroundColor: COLORS.primary,

        borderLeftWidth: 1,

        borderRightWidth: 1,

        borderBottomWidth: 1,

        borderColor: COLORS.primary
    },


    /* ========================================================
       FOOTER
    ======================================================== */

    footer: {
        position: "absolute",

        left: 28,
        right: 28,

        bottom: 18
    },

    footerLine: {
        borderTopWidth: 1,

        borderTopColor: COLORS.orange,

        marginBottom: 6
    },

    footerRow: {
        flexDirection: "row",

        justifyContent: "space-between",

        alignItems: "center"
    },

    footerText: {
        fontSize: 7,

        color: COLORS.textLight
    },

    footerSmallText: {
        marginTop: 2,

        fontSize: 7,

        color: COLORS.gray
    }

});


/* ============================================================
   CURRENCY HELPERS
============================================================ */

const getPaymentCurrency = payment => {

    return payment?.payment_currency === "USD"
        ? "USD"
        : "LRD";

};


const getPaymentAmountInLRD = payment => {

    const amountLrd = Number(
        payment?.amount_paid_lrd || 0
    );

    if (amountLrd > 0) {
        return amountLrd;
    }

    return Number(
        payment?.amount_paid || 0
    );

};


const money = value => {

    return Number(
        value || 0
    ).toLocaleString(
        undefined,
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }
    );

};


const moneyWithCurrency = (
    value,
    currency = "LRD"
) => {

    return `${currency} ${money(value)}`;

};


const today = () =>
    new Date().toLocaleDateString();


const now = () =>
    new Date().toLocaleTimeString();


/* ============================================================
   PDF HEADER
============================================================ */

const PDFHeader = ({
    gradeName
}) => (

    <View
        wrap={false}
        style={styles.headerContainer}
    >

        <View
            style={styles.headerTop}
        >

            <Image
                src={SchoolLogo}
                style={styles.logo}
            />

            <View
                style={styles.schoolDetails}
            >

                <Text
                    style={styles.schoolName}
                >
                    Exellent Kids Foundation
                </Text>

                <Text
                    style={styles.schoolAddress}
                >
                    Excellence • Integrity • Leadership
                </Text>

                <Text
                    style={styles.schoolPhone}
                >
                    Student Financial Payment Report
                </Text>

                <Text
                    style={styles.reportTitle}
                >
                    FINANCIAL PAYMENT RECORDS
                </Text>

            </View>

        </View>


        <View
            style={styles.divider}
        />


        <View
            style={styles.infoRow}
        >

            <View
                style={styles.infoBox}
            >

                <Text
                    style={styles.infoLabel}
                >
                    Grade Class
                </Text>

                <Text
                    style={styles.infoValue}
                >
                    {gradeName}
                </Text>

            </View>


            <View
                style={styles.infoBox}
            >

                <Text
                    style={styles.infoLabel}
                >
                    Generated
                </Text>

                <Text
                    style={styles.infoValue}
                >
                    {today()} {now()}
                </Text>

            </View>

        </View>

    </View>

);


/* ============================================================
   SUMMARY CARDS
============================================================ */

const SummaryCards = ({
    payments
}) => {

    const totalFee = payments.reduce(
        (
            sum,
            payment
        ) =>
            sum +
            Number(
                payment.total_fee || 0
            ),
        0
    );


    const amountPaidLrd =
        payments.reduce(
            (
                sum,
                payment
            ) =>
                sum +
                getPaymentAmountInLRD(
                    payment
                ),
            0
        );


    const balance =
        payments.reduce(
            (
                sum,
                payment
            ) =>
                sum +
                Number(
                    payment.balance_fee || 0
                ),
            0
        );


    return (

        <View
            wrap={false}
            style={
                styles.summaryContainer
            }
        >

            {/* TOTAL ENTRY */}

            <View
                style={[
                    styles.summaryCard,
                    styles.summaryBlue
                ]}
            >

                <Text
                    style={
                        styles.summaryTitle
                    }
                >
                    TOTAL ENTRY
                </Text>

                <Text
                    style={[
                        styles.summaryValue,
                        {
                            color:
                                COLORS.blue
                        }
                    ]}
                >
                    {payments.length}
                </Text>

            </View>


            {/* TOTAL FEES */}

            <View
                style={[
                    styles.summaryCard,
                    styles.summaryOrange
                ]}
            >

                <Text
                    style={
                        styles.summaryTitle
                    }
                >
                    TOTAL FEES (LRD)
                </Text>

                <Text
                    style={[
                        styles.summaryValue,
                        {
                            color:
                                COLORS.orangeDark
                        }
                    ]}
                >
                    {moneyWithCurrency(
                        totalFee,
                        "LRD"
                    )}
                </Text>

            </View>


            {/* COLLECTED */}

            <View
                style={[
                    styles.summaryCard,
                    styles.summaryGreen
                ]}
            >

                <Text
                    style={
                        styles.summaryTitle
                    }
                >
                    COLLECTED (LRD)
                </Text>

                <Text
                    style={[
                        styles.summaryValue,
                        {
                            color:
                                COLORS.green
                        }
                    ]}
                >
                    {moneyWithCurrency(
                        amountPaidLrd,
                        "LRD"
                    )}
                </Text>

            </View>


            {/* BALANCE */}

            <View
                style={[
                    styles.summaryCard,
                    styles.summaryRed
                ]}
            >

                <Text
                    style={
                        styles.summaryTitle
                    }
                >
                    BALANCE (LRD)
                </Text>

                <Text
                    style={[
                        styles.summaryValue,
                        {
                            color:
                                COLORS.red
                        }
                    ]}
                >
                    {moneyWithCurrency(
                        balance,
                        "LRD"
                    )}
                </Text>

            </View>

        </View>

    );

};


/* ============================================================
   TABLE HEADER
============================================================ */

const PaymentTableHeader = () => (

    <View
        fixed
        style={
            styles.tableHeader
        }
    >

        <Text
            style={[
                styles.tableHeaderCell,
                styles.c1
            ]}
        >
            #
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c2
            ]}
        >
            STUDENT
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c3
            ]}
        >
            CLASS
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c4
            ]}
        >
            SEMESTER
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c5
            ]}
        >
            INSTALLMENT
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c6
            ]}
        >
            TOTAL FEE
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c7
            ]}
        >
            PAID
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c8
            ]}
        >
            BALANCE
        </Text>


        <Text
            style={[
                styles.tableHeaderCell,
                styles.c9
            ]}
        >
            DATE
        </Text>

    </View>

);


/* ============================================================
   PAYMENT ROW
============================================================ */

const PaymentRow = ({
    payment,
    index
}) => {

    const balance =
        Number(
            payment.balance_fee || 0
        );


    const currency =
        getPaymentCurrency(
            payment
        );


    const amountPaid =
        Number(
            payment.amount_paid || 0
        );


    const amountPaidLrd =
        getPaymentAmountInLRD(
            payment
        );


    const exchangeRate =
        Number(
            payment.exchange_rate || 0
        );


    return (

        <View
            wrap={false}
            style={[
                styles.row,

                index % 2 === 0
                    ? styles.alternateRow
                    : {}
            ]}
        >

            {/* NUMBER */}

            <Text
                style={[
                    styles.cell,
                    styles.c1
                ]}
            >
                {index + 1}
            </Text>


            {/* STUDENT */}

            <Text
                style={[
                    styles.cell,
                    styles.c2,
                    styles.studentName
                ]}
            >
                {payment.student_name || "N/A"}
            </Text>


            {/* CLASS */}

            <Text
                style={[
                    styles.cell,
                    styles.c3
                ]}
            >
                {payment.student_class_name || "-"}
            </Text>


            {/* SEMESTER */}

            <Text
                style={[
                    styles.cell,
                    styles.c4
                ]}
            >
                {payment.semester || "-"}
            </Text>


            {/* INSTALLMENT */}

            <Text
                style={[
                    styles.cell,
                    styles.c5,
                    {
                        color:
                            COLORS.orangeDark,

                        fontWeight:
                            "bold"
                    }
                ]}
            >
                {payment.installment || "-"}
            </Text>


            {/* TOTAL FEE */}

            <Text
                style={[
                    styles.cell,
                    styles.c6
                ]}
            >
                {moneyWithCurrency(
                    payment.total_fee,
                    "LRD"
                )}
            </Text>


            {/* AMOUNT PAID */}

            <View
                style={[
                    styles.cell,
                    styles.c7,
                    {
                        alignItems:
                            "center",

                        justifyContent:
                            "center"
                    }
                ]}
            >

                <Text
                    style={
                        styles.amountPaid
                    }
                >
                    {moneyWithCurrency(
                        amountPaid,
                        currency
                    )}
                </Text>


                {currency === "USD" && (

                    <Text
                        style={
                            styles.amountPaidLrd
                        }
                    >
                        ≈ LRD{" "}
                        {money(
                            amountPaidLrd
                        )}
                    </Text>

                )}


                {currency === "USD" &&
                    exchangeRate > 0 && (

                        <Text
                            style={
                                styles.currencyLabel
                            }
                        >
                            1 USD ={" "}
                            {money(
                                exchangeRate
                            )}{" "}
                            LRD
                        </Text>

                    )}

            </View>


            {/* BALANCE */}

            <Text
                style={[
                    styles.cell,
                    styles.c8,
                    {
                        color:
                            balance > 0
                                ? COLORS.red
                                : COLORS.green,

                        fontWeight:
                            "bold"
                    }
                ]}
            >
                {moneyWithCurrency(
                    balance,
                    "LRD"
                )}
            </Text>


            {/* DATE */}

            <Text
                style={[
                    styles.cell,
                    styles.c9
                ]}
            >
                {payment.date || "-"}
            </Text>

        </View>

    );

};


/* ============================================================
   GRAND TOTAL ROW
============================================================ */

const PaymentTotals = ({
    payments
}) => {

    const totalFee =
        payments.reduce(
            (
                sum,
                payment
            ) =>
                sum +
                Number(
                    payment.total_fee || 0
                ),
            0
        );


    const totalPaidLrd =
        payments.reduce(
            (
                sum,
                payment
            ) =>
                sum +
                getPaymentAmountInLRD(
                    payment
                ),
            0
        );


    const totalBalance =
        payments.reduce(
            (
                sum,
                payment
            ) =>
                sum +
                Number(
                    payment.balance_fee || 0
                ),
            0
        );


    return (

        <View
            wrap={false}
            style={
                styles.totalRow
            }
        >

            {/* FIRST 5 COLUMNS = 57% */}

            <Text
                style={[
                    styles.cell,
                    {
                        width: "57%",

                        color:
                            COLORS.white,

                        fontWeight:
                            "bold",

                        textAlign:
                            "left",

                        paddingLeft: 10,

                        fontSize: 8
                    }
                ]}
            >
                GRAND TOTAL
            </Text>


            {/* TOTAL FEE = 11% */}

            <Text
                style={[
                    styles.cell,
                    {
                        width: "11%",

                        color:
                            COLORS.white,

                        fontWeight:
                            "bold"
                    }
                ]}
            >
                {moneyWithCurrency(
                    totalFee,
                    "LRD"
                )}
            </Text>


            {/* PAID = 11% */}

            <Text
                style={[
                    styles.cell,
                    {
                        width: "11%",

                        color:
                            "#4ade80",

                        fontWeight:
                            "bold"
                    }
                ]}
            >
                {moneyWithCurrency(
                    totalPaidLrd,
                    "LRD"
                )}
            </Text>


            {/* BALANCE = 11% */}

            <Text
                style={[
                    styles.cell,
                    {
                        width: "11%",

                        color:
                            "#f87171",

                        fontWeight:
                            "bold"
                    }
                ]}
            >
                {moneyWithCurrency(
                    totalBalance,
                    "LRD"
                )}
            </Text>


            {/* DATE COLUMN = 10% */}

            <Text
                style={[
                    styles.cell,
                    {
                        width: "10%",

                        color:
                            COLORS.white,

                        fontWeight:
                            "bold"
                    }
                ]}
            >
                TOTAL
            </Text>

        </View>

    );

};


/* ============================================================
   PDF FOOTER
============================================================ */

const PDFFooter = () => (

    <View
        fixed
        style={
            styles.footer
        }
    >

        <View
            style={
                styles.footerLine
            }
        />


        <View
            style={
                styles.footerRow
            }
        >

            <View>

                <Text
                    style={
                        styles.footerText
                    }
                >
                    Polu Integrated School Management System
                </Text>


                <Text
                    style={
                        styles.footerSmallText
                    }
                >
                    Student Financial Payment Report - SICSS
                </Text>

            </View>


            <Text
                style={
                    styles.footerText
                }
                render={({
                    pageNumber,
                    totalPages
                }) =>
                    `Page ${pageNumber} of ${totalPages}`
                }
            />

        </View>

    </View>

);


/* ============================================================
   PAYMENT DOCUMENT
============================================================ */

/*
    IMPORTANT:

    DO NOT wrap all rows inside one large table View.

    React-PDF needs each row to be an independent
    paginatable element.

    This allows:

    PAGE 1
    ─────────────────────
    Header
    Summary
    Table Header
    Row 1
    Row 2
    ...
    Row 10


    PAGE 2
    ─────────────────────
    Table Header
    Row 11
    Row 12
    Row 13
    Row 14
    Grand Total


    The fixed table header will repeat correctly.
*/

const PaymentDocument = ({
    payments,
    gradeName
}) => (

    <Document>

        <Page
            size="A4"
            orientation="landscape"
            style={
                styles.page
            }
            wrap
        >

            {/* ================= HEADER ================= */}

            <PDFHeader
                gradeName={
                    gradeName
                }
            />


            {/* ================= SUMMARY ================= */}

            <SummaryCards
                payments={
                    payments
                }
            />


            {/* ================= TABLE HEADER ================= */}

            <PaymentTableHeader />


            {/* ================= PAYMENT ROWS ================= */}

            <View
                style={
                    styles.tableSection
                }
            >

                {payments.map(
                    (
                        payment,
                        index
                    ) => (

                        <PaymentRow
                            key={
                                payment.id ||
                                `${payment.student_name}-${index}`
                            }
                            payment={
                                payment
                            }
                            index={
                                index
                            }
                        />

                    )
                )}

            </View>


            {/* ================= GRAND TOTAL ================= */}

            <PaymentTotals
                payments={
                    payments
                }
            />


            {/* ================= FOOTER ================= */}

            <PDFFooter />

        </Page>

    </Document>

);


/* ============================================================
   GENERATE PAYMENT PDF
============================================================ */

export const generatePaymentPDF = async ({
    selectedGrade,
    grades,
    token,
    api,
    setLoading,
    setError
}) => {

    /* ================= VALIDATE GRADE ================= */

    if (!selectedGrade) {

        Swal.fire({
            icon: "warning",

            title: "Grade Required",

            text:
                "Please select a grade class before generating the report."
        });

        return;

    }


    /* ================= VALIDATE TOKEN ================= */

    if (!token) {

        Swal.fire({
            icon: "error",

            title:
                "Authentication Error",

            text:
                "Authentication token not found."
        });

        return;

    }


    try {

        setLoading(true);

        let payments = [];

        let page = 1;


        /* ====================================================
           FETCH ALL PAGINATED PAYMENT RECORDS
        ==================================================== */

        while (true) {

            const response =
                await api.get(
                    "/api/users/payments/",
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        },

                        params: {
                            student_class:
                                selectedGrade,

                            page,

                            page_size:
                                100,

                            exclude_blacklisted:
                                true
                        }
                    }
                );


            const currentPage =
                response.data.results || [];


            payments = [
                ...payments,
                ...currentPage
            ];


            /*
                Stop when Django REST Framework
                says there is no next page.
            */

            if (
                !response.data.next
            ) {
                break;
            }


            page++;

        }


        /* ====================================================
           NO PAYMENTS
        ==================================================== */

        if (
            !payments.length
        ) {

            Swal.fire({
                icon: "info",

                title:
                    "No Payment Records",

                text:
                    "There are no payment records for the selected class."
            });

            return;

        }


        /* ====================================================
           SORT STUDENTS
        ==================================================== */

        payments.sort(
            (
                a,
                b
            ) =>
                (
                    a.student_name ||
                    ""
                ).localeCompare(
                    b.student_name ||
                    ""
                )
        );


        /* ====================================================
           GET GRADE NAME
        ==================================================== */

        const gradeName =
            grades.find(
                grade =>
                    Number(
                        grade.id
                    ) ===
                    Number(
                        selectedGrade
                    )
            )?.name ||
            "Unknown Grade";


        /* ====================================================
           GENERATE PDF
        ==================================================== */

        const blob =
            await pdf(

                <PaymentDocument
                    payments={
                        payments
                    }
                    gradeName={
                        gradeName
                    }
                />

            ).toBlob();


        /* ====================================================
           CREATE DOWNLOAD URL
        ==================================================== */

        const url =
            URL.createObjectURL(
                blob
            );


        const link =
            document.createElement(
                "a"
            );


        const currentDate =
            new Date();


        const fileDate =
            `${currentDate.getFullYear()}-${String(
                currentDate.getMonth() + 1
            ).padStart(
                2,
                "0"
            )}-${String(
                currentDate.getDate()
            ).padStart(
                2,
                "0"
            )}`;


        link.href =
            url;


        link.download =
            `Financial_Payment_Report_${gradeName}_${fileDate}.pdf`;


        document.body.appendChild(
            link
        );


        link.click();


        document.body.removeChild(
            link
        );


        /*
            Delay URL cleanup slightly.

            This is safer in production browsers
            than immediately revoking the Blob URL.
        */

        setTimeout(
            () => {

                URL.revokeObjectURL(
                    url
                );

            },
            1000
        );


        /* ====================================================
           SUCCESS
        ==================================================== */

        Swal.fire({
            icon: "success",

            title:
                "PDF Generated",

            text:
                "Financial payment report downloaded successfully.",

            timer:
                1800,

            showConfirmButton:
                false
        });

    } catch (
        error
    ) {

        console.error(
            "PDF GENERATION ERROR:",
            error
        );


        setError(
            "Failed to generate payment report."
        );


        Swal.fire({
            icon: "error",

            title:
                "Generation Failed",

            text:
                "Unable to generate the payment report."
        });

    } finally {

        setLoading(false);

    }

};